import React, { useEffect, useState, useRef, useMemo } from "react";
import * as XLSX from "xlsx-js-style";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useMessages } from "context/messages";
import { getToken } from "context/auth";

import Widget from "components/dashboard/widget";
import ButtonText from "components/common/buttonText";
import Stepper from "components/common/stepper";
import Paging from "components/common/paging";

import { getObjectIdsForArea } from "api/objectIds";
import { bulkAssign } from "api/assignment";

import { validateAssignmentRow, validateTransitions, parseDate, validatePhone, formatExistingPermitLabel } from "utils/uploadValidation";

import ImportFromExternalSystem from "./importFromExternalSystem";

// 10 kolumner: 4 read-only (grå) + 6 editable (vita).
// Patrik 2026-05-05: ParkingSpotId (DB-uppräknare) + Landskod borttagna,
// "Har aktivt tillstånd" + "t.o.m." sammanslagna till "Befintligt tillstånd".
// Slice 04 (2026-05-23): Pris + Sublet-kolumner tillagda per målbild §4.3.
const TEMPLATE_COLUMNS = [
  "Plats-ID",
  "Objekt-id",
  "Platsnummer",
  "Befintligt tillstånd",
  "Telefonnummer",
  "Regnummer",
  "Från-datum",
  "Till-datum",
  "Pris (kr/månad)",
  "Annan part (Ja/Nej)",
];
const READ_ONLY_COLUMN_INDEXES = new Set([0, 1, 2, 3]);

const ACTION_LABEL = {
  Created:     { sv: "Skapas",       en: "Will be created", color: "#16a34a", bg: "#dcfce7" },
  Updated:     { sv: "Avslutas",     en: "Will end",        color: "#0369a1", bg: "#e0f2fe" },
  Transition:  { sv: "Övergång",     en: "Transition",      color: "#7c3aed", bg: "#ede9fe" },
  DormantOnly: { sv: "Vilande user", en: "Dormant user",    color: "#a16207", bg: "#fef9c3" },
  NoChange:    { sv: "Ingen ändring",en: "No change",       color: "#6b7280", bg: "#f3f4f6" },
  Ignored:     { sv: "Ignoreras",    en: "Ignored",         color: "#9ca3af", bg: "#f9fafb" },
  Error:       { sv: "Fel",          en: "Error",           color: "#dc2626", bg: "#fee2e2" },
  OverlapBlock:{ sv: "Blockerad",    en: "Blocked",         color: "#dc2626", bg: "#fee2e2" },
};

const AssignmentPage = () => {
  const { t, i18n } = useTranslation();
  const { showToast } = useMessages();
  const { parkingAreaExtId } = useParams();

  const [spots, setSpots] = useState([]);
  const [rawRows, setRawRows] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [dryRunPage, setDryRunPage] = useState(0);
  // editedRowIndexes spårar vilka rader som ändrats i portalen efter upload.
  // Används för: (a) "edited"-badge på rad, (b) "Ladda ner uppdaterad mall"-knapp,
  // (c) signal till användaren att Excel-källfilen är out-of-sync.
  const [editedRowIndexes, setEditedRowIndexes] = useState(new Set());
  const fileInputRef = useRef(null);
  const PAGE_SIZE = 100;

  // updateRow uppdaterar EN cell i rawRows + invaliderar tidigare dry-run-resultat
  // (eftersom backend nu skulle returnera annat med nya data). Användaren måste
  // klicka "Kör om förhandsgranskning" igen.
  const updateRow = (idx, field, value) => {
    setRawRows((prev) => {
      if (!prev) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    setEditedRowIndexes((prev) => {
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
    setDryRunResult(null);
    setDryRunPage(0);
  };

  const lang = (i18n.language || "sv").startsWith("en") ? "en" : "sv";

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const data = await getObjectIdsForArea(token, parkingAreaExtId);
        setSpots(data || []);
      } catch (e) {
        showToast(t("assignment.loadFailed"), "", "error");
      }
    })();
  }, [parkingAreaExtId]);

  // Hämta extId från en rad — tål både "Plats-ID" och "🔒 Plats-ID" som header.
  // Definieras FÖRE clientValidation-useMemo nedan eftersom const-arrow inte hoistas.
  const getExtIdFromRow = (row) =>
    (row["Plats-ID"] ?? row["🔒 Plats-ID"] ?? "").toString().trim();

  // Klient-side validering: kör per-rad-checkar + cross-row-transitioner
  // INNAN backend-anrop. Tre kategorier per rad:
  //   - ignored:    inget telefonnummer → backend skippar raden
  //   - dormantOnly: telefon men inga datum → backend skapar vilande user (inget tillstånd)
  //   - activate:   telefon + minst ett datum → tillståndet ska skapas/uppdateras (validera datum)
  const clientValidation = useMemo(() => {
    if (!rawRows) return null;
    const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";
    // Klient-validering är medvetet PERMISSIV — vi fångar bara format-fel
    // (ogiltigt datum, telefon-syntax, rimligt år-intervall). "Är detta
    // faktiskt tillåtet?"-frågan svarar backend på via Kontrollera-knappen.
    const classifyRow = (row) => {
      if (!hasValue(row["Telefonnummer"])) return "ignored";
      const hasFrom = hasValue(row["Från-datum"]);
      const hasTo = hasValue(row["Till-datum"]);
      if (!hasFrom && !hasTo) return "dormantOnly";
      return "activate";
    };

    const perRow = rawRows.map((row) => {
      const kind = classifyRow(row);
      if (kind === "ignored") {
        return { kind, errors: [], warnings: [], cellErrors: {}, cellWarnings: {}, parsed: {} };
      }
      // Phone-format-validering körs både för dormantOnly och activate
      const phoneResult = validatePhone(row["Telefonnummer"]);
      const cellErrors = {};
      const phoneErrors = [];
      if (phoneResult.error) {
        cellErrors.phone = phoneResult.error;
        phoneErrors.push(phoneResult.error);
      }
      if (kind === "dormantOnly") {
        return { kind, errors: phoneErrors, warnings: [], cellErrors, cellWarnings: {}, parsed: {} };
      }

      const dateResult = validateAssignmentRow({
        startDate: row["Från-datum"],
        endDate: row["Till-datum"],
      });
      return {
        kind,
        errors: [...phoneErrors, ...dateResult.errors],
        warnings: dateResult.warnings,
        cellErrors: { ...cellErrors, ...(dateResult.fieldErrors || {}) },
        cellWarnings: dateResult.fieldWarnings || {},
        parsed: dateResult.parsed,
      };
    });

    // Cross-row övergångar gäller bara activate-rader
    const transitionInput = rawRows.map((row, idx) => ({
      spotId: perRow[idx].kind === "activate" ? getExtIdFromRow(row) : null,
      parsedStartDate: perRow[idx].parsed?.startDate,
      parsedEndDate: perRow[idx].parsed?.endDate,
      phone: row["Telefonnummer"],
    }));
    const transitionIssues = validateTransitions(transitionInput);

    const errors = [];
    const warnings = [];
    const rowResults = rawRows.map((_, idx) => {
      const r = perRow[idx];
      const trans = transitionIssues[idx] || { errors: [], warnings: [], fieldErrors: {}, fieldWarnings: {} };
      // Merga radens egna cell-fel med övergångs-cell-fel
      const cellErrors = { ...r.cellErrors, ...trans.fieldErrors };
      const cellWarnings = { ...r.cellWarnings, ...trans.fieldWarnings };
      // Bygg summerings-listor (för fel/varnings-boxarna ovanför tabellen)
      r.errors.forEach((msg) => errors.push({ row: idx + 2, msg }));
      r.warnings.forEach((msg) => warnings.push({ row: idx + 2, msg }));
      trans.errors.forEach((msg) => errors.push({ row: idx + 2, msg }));
      trans.warnings.forEach((msg) => warnings.push({ row: idx + 2, msg }));
      return { kind: r.kind, cellErrors, cellWarnings };
    });

    return { errors, warnings, hasErrors: errors.length > 0, rowResults };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawRows]);

  // Natural sort på Platsnummer så A2 < A10 (default alfabetisk gör A10 < A2).
  // Använder Intl-collator med numeric:true — bryter ut nummer i strängen och jämför numeriskt.
  const naturalCompareSpot = (a, b) =>
    String(a.spotIdentifier || "").localeCompare(String(b.spotIdentifier || ""), "sv", { numeric: true, sensitivity: "base" });

  const handleDownload = () => {
    const rows = spots
      .slice()
      .sort(naturalCompareSpot)
      .map((s) => {
        const existing = formatExistingPermitLabel(s);
        return {
          "Plats-ID": s.parkingSpotExtId,
          "Objekt-id": s.objectID || "",
          "Platsnummer": s.spotIdentifier || "",
          "Befintligt tillstånd": existing,
          "Telefonnummer": s.activePermitPhone || "",
          "Regnummer": "",
          "Från-datum": "",
          "Till-datum": "",
          // Slice 04 (2026-05-23): Pris+Sublet-kolumner per målbild §4.3
          "Pris (kr/månad)": "",
          "Annan part (Ja/Nej)": "",
        };
      });
    const ws = XLSX.utils.json_to_sheet(rows, { header: TEMPLATE_COLUMNS });

    // Pre-fyll tomma celler i Telefonnummer-kolumnen (E, index 4) med text-format (z:'@')
    // så Excel/LibreOffice inte coercar "+46..." till "46..." (tal utan +).
    // Målbild §4.3: dynamiskt radantal = exakt antal data-rader (= antal tillgängliga
    // objekt i området). Idag hårdkodat 500 → tomma rader i mallen.
    const PHONE_COL_INDEX = 4;
    const TEMPLATE_ROW_COUNT = rows.length;
    const existingRange = XLSX.utils.decode_range(ws["!ref"] || "A1");
    for (let r = 1; r <= TEMPLATE_ROW_COUNT; r++) {
      const addr = XLSX.utils.encode_cell({ r, c: PHONE_COL_INDEX });
      if (!ws[addr] || (ws[addr].v === undefined || ws[addr].v === "")) {
        ws[addr] = { t: 's', v: '', z: '@' };
      } else {
        ws[addr].z = '@';
        ws[addr].t = 's';
      }
    }
    if (TEMPLATE_ROW_COUNT > 0) {
      ws['!ref'] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: Math.max(existingRange.e.r, TEMPLATE_ROW_COUNT), c: existingRange.e.c },
      });
    }

    ws['!dataValidation'] = [
      {
        sqref: 'E2:E1000',
        type: 'custom',
        formula1: 'OR(LEN(E2)=0, AND(LEFT(E2,1)="+", LEN(E2)>=10), AND(LEFT(E2,2)="07", LEN(E2)=10))',
        errorTitle: 'Ogiltigt telefonnummer',
        error: 'Telefonnummer ska vara i format +46701234567 eller 0701234567.',
      },
      {
        sqref: 'G2:G1000',
        type: 'date',
        operator: 'greaterThan',
        formula1: '2020-01-01',
        errorTitle: 'Ogiltigt datum',
        error: 'Från-datum måste vara efter 2020-01-01.',
      },
      {
        sqref: 'H2:H1000',
        type: 'date',
        operator: 'greaterThan',
        formula1: '2020-01-01',
        errorTitle: 'Ogiltigt datum',
        error: 'Till-datum måste vara efter 2020-01-01.',
      },
      // Slice 04 (2026-05-23): Pris ≥ 0 (heltal i SEK)
      {
        sqref: 'I2:I1000',
        type: 'whole',
        operator: 'greaterThanOrEqual',
        formula1: '0',
        errorTitle: 'Ogiltigt pris',
        error: 'Pris ska vara ett heltal ≥ 0 (kr/månad). Lämna tom för 0 kr.',
      },
      // Annan part "Ja" / "Nej" / tom (default Nej = AirPark). Ja → fakturerad externt (grupp C, pris nollas).
      {
        sqref: 'J2:J1000',
        type: 'list',
        formula1: '"Ja,Nej"',
        errorTitle: 'Ogiltigt värde',
        error: 'Annan part ska vara "Ja" eller "Nej" (tom = Nej).',
      },
    ];

    const lockedHeaders = { A1: "🔒 Plats-ID", B1: "🔒 Objekt-id", C1: "🔒 Platsnummer", D1: "🔒 Befintligt tillstånd" };
    Object.entries(lockedHeaders).forEach(([addr, val]) => { if (ws[addr]) ws[addr].v = val; });

    const LOCKED_DATA_FILL = { fgColor: { rgb: "D1D5DB" } };
    const LOCKED_HEADER_FILL = { fgColor: { rgb: "9CA3AF" } };
    const EDITABLE_HEADER_FILL = { fgColor: { rgb: "F3F4F6" } };
    const HEADER_FONT = { bold: true };

    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[addr]) ws[addr] = { t: "s", v: "" };
        const isHeader = R === 0;
        const isLocked = READ_ONLY_COLUMN_INDEXES.has(C);
        if (isHeader) {
          ws[addr].s = { font: HEADER_FONT, fill: isLocked ? LOCKED_HEADER_FILL : EDITABLE_HEADER_FILL };
        } else if (isLocked) {
          ws[addr].s = { fill: LOCKED_DATA_FILL };
        }
      }
    }
    ws["!cols"] = [
      { wch: 18 }, { wch: 16 }, { wch: 18 }, { wch: 26 },
      { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 14 }, { wch: 22 },
    ];

    const describe = [
      { Kolumn: 'Plats-ID', Status: '🔒 Låst', Beskrivning: 'AirParks interna nyckel. Ändra inte — rader med ändrat värde hoppas över.' },
      { Kolumn: 'Objekt-id', Status: '🔒 Låst', Beskrivning: 'Fastighetsägarens egen identifierare. Ändras på Objekt-id-sidan.' },
      { Kolumn: 'Platsnummer', Status: '🔒 Låst', Beskrivning: 'Visningsnamn på platsen. Ändras på Objekt-id-sidan.' },
      { Kolumn: 'Befintligt tillstånd', Status: '🔒 Låst', Beskrivning: 'Informativt. "Inget" / "Tillsvidare" / slutdatum.' },
      { Kolumn: 'Telefonnummer', Status: '✏️ Ändringsbar', Beskrivning: 'Fylls av fastighetsägaren. Format +46701234567 eller 0701234567. Tomt = raden ignoreras. Förifyllt om plats redan har aktivt tillstånd.' },
      { Kolumn: 'Regnummer', Status: '✏️ Ändringsbar', Beskrivning: 'Valfritt. Förifyller fordon på tillståndet.' },
      { Kolumn: 'Från-datum', Status: '✏️ Ändringsbar', Beskrivning: 'ISO 8601 (YYYY-MM-DD).' },
      { Kolumn: 'Till-datum', Status: '✏️ Ändringsbar', Beskrivning: 'ISO 8601. Måste vara ≥ Från-datum. Tomt = tillsvidare.' },
      { Kolumn: 'Pris (kr/månad)', Status: '✏️ Ändringsbar', Beskrivning: 'Heltal ≥ 0 i SEK. Tom = 0 kr. Vid >0 → kunden måste välja betalmetod i appen vid aktivering (PermitActivationGuard).' },
      { Kolumn: 'Annan part (Ja/Nej)', Status: '✏️ Ändringsbar', Beskrivning: 'Ja = faktureras av annan part (ej AirPark). Då ignoreras Pris (sätts tomt). Default = Nej (om tom) = AirPark.' },
    ];
    const wsDescribe = XLSX.utils.json_to_sheet(describe);
    wsDescribe["!cols"] = [{ wch: 22 }, { wch: 16 }, { wch: 80 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tilldelning");
    XLSX.utils.book_append_sheet(wb, wsDescribe, "Beskrivning");
    XLSX.writeFile(wb, `tilldelning_${parkingAreaExtId}.xlsx`);
  };

  const findDuplicates = (rows) => {
    const groups = new Map();
    rows.forEach((row, idx) => {
      const objId = (row["Objekt-id"] ?? "").toString().trim();
      const phone = (row["Telefonnummer"] || "").toString().trim().replace(/[\s\-()]/g, "");
      if (!objId || !phone) return;
      let normPhone = phone;
      if (phone.startsWith("0")) normPhone = "+46" + phone.slice(1);
      const key = `${objId.toLowerCase()}|${normPhone}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(idx + 2);
    });
    return Array.from(groups.entries())
      .filter(([, lines]) => lines.length > 1)
      .map(([key, lines]) => ({ key, lines }));
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      // cellDates: true → datum-celler returneras som Date-objekt istället för
      // Excel-serial-nummer (typ 46142). Utan denna flagga var det "2026-05-01"
      // i Excel som blev talet 46142, som new Date("46142") tolkar som år 46142.
      const wb = XLSX.read(data, { cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);

      const knownExtIds = new Set(spots.map((s) => s.parkingSpotExtId));
      const unknownIds = [];
      rows.forEach((row, idx) => {
        const extId = getExtIdFromRow(row);
        if (extId && !knownExtIds.has(extId)) {
          unknownIds.push({ row: idx + 2, extId });
        }
      });
      if (unknownIds.length > 0) {
        const list = unknownIds.slice(0, 5).map(u => `rad ${u.row}: '${u.extId}'`).join(", ");
        const more = unknownIds.length > 5 ? ` +${unknownIds.length - 5} till` : "";
        showToast(t("assignment.unknownExtIdError", { details: list + more }), "", "error");
        if (fileInputRef.current) fileInputRef.current.value = "";
        setRawRows(null);
        setDryRunResult(null);
        return;
      }

      const dupes = findDuplicates(rows);
      if (dupes.length > 0) {
        const msg = dupes
          .map(d => {
            const [objId, phone] = d.key.split("|");
            return t("assignment.duplicateRowError", { rows: d.lines.join(", "), objectId: objId, phone });
          })
          .join(" ");
        showToast(msg, "", "error");
        if (fileInputRef.current) fileInputRef.current.value = "";
        setRawRows(null);
        setDryRunResult(null);
        return;
      }

      setRawRows(rows);
      setDryRunResult(null);
      setActiveStep(2);
    } catch (err) {
      showToast(t("assignment.parseFailed"), "", "error");
    }
  };

  const rowsToPayload = () => {
    const extIdToId = new Map(spots.map((s) => [s.parkingSpotExtId, s.parkingSpotId]));
    return (rawRows || []).map((row) => {
      const extId = getExtIdFromRow(row);
      // parseDate hanterar Excel-serial (number eller numerisk-sträng) korrekt.
      // Innan: new Date(46142) → 1970-01-01 (tolkades som ms sedan epoch).
      const fromDate = parseDate(row["Från-datum"]);
      const toDate = parseDate(row["Till-datum"]);
      // Slice 04 (2026-05-23): Pris + Sublet per rad per målbild §4.3.
      // Tom Pris-cell → 0 (gratis-permit). Tom Sublet-cell → false (default Nej).
      const priceRaw = row["Pris (kr/månad)"];
      const priceValue = priceRaw === "" || priceRaw == null
        ? null
        : Number(priceRaw);
      // "Annan part (Ja/Nej)": Ja → faktureras externt (grupp C/R3). Tom/Nej/saknad kolumn → AirPark (oförändrat).
      const manualRaw = (row["Annan part (Ja/Nej)"] || "").toString().trim().toLowerCase();
      const isPaymentHandledManually = manualRaw === "ja" || manualRaw === "yes";

      return {
        parkingSpotId: extIdToId.get(extId) || 0,
        parkingSpotExtId: extId,
        objectID: (row["Objekt-id"] ?? "").toString().trim() || null,
        phone: (row["Telefonnummer"] || "").toString().trim() || null,
        countryCode: "+46",
        registrationIdentifier: (row["Regnummer"] || "").toString().trim() || null,
        fromDate: fromDate ? fromDate.toISOString() : null,
        toDate: toDate ? toDate.toISOString() : null,
        // R3: Annan part → Price MÅSTE vara null. Annars befintlig logik (>0 → pris, annars null → backend 0).
        price: isPaymentHandledManually ? null : (Number.isFinite(priceValue) && priceValue > 0 ? priceValue : null),
        isPaymentHandledManually,
      };
    });
  };

  const handleDryRun = async () => {
    try {
      setIsRunning(true);
      const token = await getToken();
      const result = await bulkAssign(token, {
        parkingAreaExtId,
        rows: rowsToPayload(),
        dryRun: true,
      });
      setDryRunResult(result);
      setDryRunPage(0);
    } catch (e) {
      showToast(t("assignment.dryRunFailed"), "", "error");
    } finally {
      setIsRunning(false);
    }
  };

  const handleExecute = async () => {
    if (!window.confirm(t("assignment.confirmExecute"))) return;
    try {
      setIsRunning(true);
      const token = await getToken();
      const result = await bulkAssign(token, {
        parkingAreaExtId,
        rows: rowsToPayload(),
        dryRun: false,
      });
      if (result.blockingErrors?.length > 0) {
        showToast(t("assignment.blockedByErrors"), "", "error");
        setDryRunResult(result);
      } else {
        const summary = `${result.newPermits} ${t("assignment.newPermits")}, ${result.updatedPermits} ${t("assignment.updatedPermits")}, ${result.transitions || 0} ${t("assignment.transitions")}, ${result.dormantUsersCreated} ${t("assignment.dormantUsers")}`;
        showToast(summary, "", "success");
        setDryRunResult(result);
        setActiveStep(3);
      }
    } catch (e) {
      showToast(t("assignment.executeFailed"), "", "error");
    } finally {
      setIsRunning(false);
    }
  };

  const handleReset = () => {
    if (rawRows && !window.confirm(t("assignment.confirmReset"))) return;
    setRawRows(null);
    setDryRunResult(null);
    setDryRunPage(0);
    setEditedRowIndexes(new Set());
    setActiveStep(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Exporterar nuvarande rawRows som Excel — så användaren kan ersätta sin källfil
  // efter inline-editing och behålla synk med Excel-systemet.
  const handleDownloadEdited = () => {
    if (!rawRows || rawRows.length === 0) return;
    // Normalisera datum-cellerna till YYYY-MM-DD så formatet förblir konsekvent
    const exportRows = rawRows.map((row) => {
      const cell = (val) => {
        if (!val) return "";
        const d = parseDate(val);
        return d ? d.toISOString().slice(0, 10) : "";
      };
      return {
        "Plats-ID": row["Plats-ID"] ?? row["🔒 Plats-ID"] ?? "",
        "Objekt-id": row["Objekt-id"] ?? row["🔒 Objekt-id"] ?? "",
        "Platsnummer": row["Platsnummer"] ?? row["🔒 Platsnummer"] ?? "",
        "Befintligt tillstånd": row["Befintligt tillstånd"] ?? row["🔒 Befintligt tillstånd"] ?? "",
        "Telefonnummer": row["Telefonnummer"] || "",
        "Regnummer": row["Regnummer"] || "",
        "Från-datum": cell(row["Från-datum"]),
        "Till-datum": cell(row["Till-datum"]),
        "Pris (kr/månad)": row["Pris (kr/månad)"] ?? "",
        "Annan part (Ja/Nej)": row["Annan part (Ja/Nej)"] ?? "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(exportRows, { header: TEMPLATE_COLUMNS });

    // Text-format på Telefonnummer-kolumnen (E, index 4) så Excel/LibreOffice
    // inte coercar "+46..." till "46..." (tal utan +). Se motsvarande logik i skapa-mall ovan.
    const PHONE_COL_INDEX_EXPORT = 4;
    const exportRange = XLSX.utils.decode_range(ws["!ref"] || "A1");
    for (let r = 1; r <= Math.max(exportRange.e.r, 500); r++) {
      const addr = XLSX.utils.encode_cell({ r, c: PHONE_COL_INDEX_EXPORT });
      if (!ws[addr] || (ws[addr].v === undefined || ws[addr].v === "")) {
        ws[addr] = { t: 's', v: ws[addr]?.v || '', z: '@' };
      } else {
        ws[addr].z = '@';
        ws[addr].t = 's';
        ws[addr].v = String(ws[addr].v);
      }
    }
    ws['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: Math.max(exportRange.e.r, 500), c: exportRange.e.c },
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tilldelning");
    XLSX.writeFile(wb, `tilldelning_${parkingAreaExtId}_editerad.xlsx`);
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toISOString().slice(0, 10);
  };

  // Inline-editable cell. Klick → input tar över → onBlur sparar. Enter sparar, Escape avbryter.
  // type="date" ger OS-native picker; type="text" för telefon/regnr.
  // error/warning är felmeddelande-strängar som rendererar röd/gul border + tooltip.
  const EditableCell = ({ value, displayValue, onSave, type = "text", placeholder, error, warning, options }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const startEdit = () => {
      if (type === "date") {
        const d = parseDate(value);
        setDraft(d ? d.toISOString().slice(0, 10) : "");
      } else {
        setDraft(value == null ? "" : String(value));
      }
      setEditing(true);
    };
    const commit = () => {
      setEditing(false);
      const newValue = draft.trim();
      const oldValue = value == null ? "" : String(value).trim();
      if (newValue !== oldValue) {
        onSave(newValue || null);
      }
    };
    // Highlight-styling när cellen har fel eller varning
    const highlightStyle = error
      ? { border: '1.5px solid #E93161', backgroundColor: 'rgba(233,49,97,0.08)' }
      : warning
        ? { border: '1.5px solid #FFAB2E', backgroundColor: 'rgba(255,171,46,0.10)' }
        : null;
    const tooltip = error || warning || (t("assignment.clickToEdit") || "Klicka för att redigera");

    if (!editing) {
      return (
        <span
          onClick={startEdit}
          className="cursor-pointer hover:bg-airpark-gray-300 px-2 py-1 rounded inline-block min-w-[40px]"
          style={highlightStyle}
          title={tooltip}
        >
          {displayValue ?? value ?? "—"}
        </span>
      );
    }
    // Enum-läge: dropdown (Ja/Nej) — användaren kan inte skriva fritext.
    if (options) {
      return (
        <select
          autoFocus
          value={draft || options[options.length - 1] || ""}
          onChange={(e) => {
            const v = e.target.value;
            setEditing(false);
            const oldValue = value == null ? "" : String(value).trim();
            if (v !== oldValue) onSave(v || null);
          }}
          onBlur={() => setEditing(false)}
          className="w-full px-2 py-1 rounded border border-airpark-green focus:outline-none text-sm bg-white"
        >
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    return (
      <input
        type={type}
        autoFocus
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); e.target.blur(); }
          if (e.key === "Escape") { setEditing(false); }
        }}
        className="w-full px-2 py-1 rounded border border-airpark-green focus:outline-none text-sm"
      />
    );
  };

  const renderActionBadge = (action) => {
    const info = ACTION_LABEL[action] || ACTION_LABEL.Ignored;
    return (
      <span style={{
        backgroundColor: info.bg,
        color: info.color,
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}>
        {info[lang]}
      </span>
    );
  };

  // Stepper-definition. Mer info-tät än Kontraktskoder eftersom Tilldelning är
  // en mer komplex pipeline (Excel-mall → upload → preview → execute).
  const STEPS = [
    { title: t("assignment.step1Title") || "Förstå format" },
    { title: t("assignment.step2Title") || "Välj fil" },
    { title: t("assignment.step3Title") || "Förhandsgranska" },
    { title: t("assignment.step4Title") || "Klart" },
  ];

  // Beräkna om Next-knappen är aktiv för aktuellt steg
  const canAdvance = () => {
    if (activeStep === 0) return true;
    if (activeStep === 1) return !!rawRows;
    if (activeStep === 2) return !!dryRunResult && !dryRunResult.wasDryRun;
    return false;
  };

  return (
    <>
      <Widget className="mb-6">
        <div className="flex flex-col">
          <h2 className="text-xl font-medium mb-1">{t("assignment.title")}</h2>
          <span className="text-airpark-gray-500 text-sm">
            {t("assignment.subtitle") || "Massuppdatering av tillstånd via Excel-fil."}
          </span>
        </div>
      </Widget>

      <Widget className="mb-6">
        <Stepper
          steps={STEPS}
          activeStep={activeStep}
          onStepClick={(idx) => {
            // Tillåt att hoppa bak till tidigare steg (caretaker återstart)
            if (idx < activeStep) setActiveStep(idx);
          }}
          className="mb-8"
        />

        {/* STEG 1: Förstå format + ladda ner mall */}
        {activeStep === 0 && (
          <div className="flex flex-col gap-4">
            <h3 className="text-base font-medium">{t("assignment.howItWorksTitle")}</h3>
            <p className="text-sm text-airpark-gray-500">{t("assignment.description")}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
              {[
                { label: t("assignment.howItWorks_create_label"), text: t("assignment.howItWorks_create") },
                { label: t("assignment.howItWorks_update_label"), text: t("assignment.howItWorks_update") },
                { label: t("assignment.howItWorks_transition_label"), text: t("assignment.howItWorks_transition") },
                { label: t("assignment.howItWorks_dormant_label"), text: t("assignment.howItWorks_dormant") },
                { label: t("assignment.howItWorks_noChange_label"), text: t("assignment.howItWorks_noChange") },
                { label: t("assignment.howItWorks_ignored_label"), text: t("assignment.howItWorks_ignored") },
                { label: t("assignment.howItWorks_phoneFormat_label"), text: t("assignment.howItWorks_phoneFormat") },
              ].map((card, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-lg bg-airpark-gray-300"
                  data-testid={`assignment-howitworks-card-${i}`}
                >
                  <span className="flex-shrink-0 w-2 h-2 rounded-full mt-1.5 bg-airpark-gray-500" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{card.label}</div>
                    <div className="text-xs mt-1 leading-snug text-airpark-gray-500">{card.text}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 mt-4">
              <ButtonText isSecondary onClick={handleDownload}>
                {t("assignment.downloadTemplate")}
              </ButtonText>
              <ButtonText onClick={() => setActiveStep(1)}>
                {t("assignment.continue") || "Fortsätt"}
              </ButtonText>
            </div>
          </div>
        )}

        {/* STEG 2: Välj fil eller importera från fastighetssystem */}
        {activeStep === 1 && (
          <div className="flex flex-col gap-4">
            <h3 className="text-base font-medium">{t("assignment.uploadStepTitle") || "Ladda upp Excel-fil"}</h3>
            <p className="text-sm text-airpark-gray-500">
              {t("assignment.uploadStepDescription") ||
                "Välj den ifyllda mallen eller importera direkt från ditt fastighetssystem."}
            </p>

            <div className="flex flex-wrap gap-3 mt-2">
              <ButtonText onClick={() => fileInputRef.current?.click()}>
                {t("assignment.uploadExcel")}
              </ButtonText>
              <ButtonText
                isSecondary
                onClick={() => setShowImportModal(true)}
                data-testid="assignment-import-from-external-button"
              >
                {t("assignment.importFromExternal")}
              </ButtonText>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleFile}
              />
            </div>

            <div className="flex gap-3 mt-6">
              <ButtonText isSecondary onClick={() => setActiveStep(0)}>
                {t("assignment.back") || "Tillbaka"}
              </ButtonText>
            </div>
          </div>
        )}

        {/* STEG 3: Förhandsgranska + bekräfta */}
        {activeStep === 2 && (
          <div className="flex flex-col gap-4">
            <h3 className="text-base font-medium">
              {t("assignment.previewStepTitle") || "Förhandsgranska tilldelningar"}
            </h3>
            <p className="text-sm text-airpark-gray-500">
              {rawRows && t("assignment.rowsLoaded")}: {rawRows?.length || 0}
            </p>

            {/* Klient-side validering — visas före dry-run.
                Sammanfattning + radtabell. Tabellen highlightar fel/varningar
                så användaren ser EXAKT vilken cell som strular istället för bara
                "Rad 3: Startdatum saknas" utan kontext. */}
            {clientValidation && clientValidation.errors.length > 0 && (
              <div className="border border-airpark-strawberry-500 rounded-lg p-4" style={{ backgroundColor: 'rgba(233,49,97,0.08)' }}>
                <div className="font-medium text-airpark-strawberry-500 mb-2">
                  {t("assignment.clientErrors") || "Fel i filen"} ({clientValidation.errors.length})
                </div>
                <ul className="text-sm text-airpark-strawberry-500 space-y-1">
                  {clientValidation.errors.slice(0, 10).map((e, i) => (
                    <li key={i}>Rad {e.row}: {e.msg}</li>
                  ))}
                  {clientValidation.errors.length > 10 && (
                    <li>... +{clientValidation.errors.length - 10} fler</li>
                  )}
                </ul>
              </div>
            )}
            {clientValidation && clientValidation.warnings.length > 0 && (
              <div className="bg-airpark-yellow-background border border-airpark-yellow rounded-lg p-4">
                <div className="font-medium text-airpark-oil-500 mb-2">
                  {t("assignment.clientWarnings") || "Varningar"} ({clientValidation.warnings.length})
                </div>
                <ul className="text-sm text-airpark-oil-500 space-y-1">
                  {clientValidation.warnings.slice(0, 10).map((w, i) => (
                    <li key={i}>Rad {w.row}: {w.msg}</li>
                  ))}
                  {clientValidation.warnings.length > 10 && (
                    <li>... +{clientValidation.warnings.length - 10} fler</li>
                  )}
                </ul>
              </div>
            )}

            {/* Klient-tabellen togs bort 2026-05-14 — visade samma rader som
                backend-dry-run-tabellen nedanför fast med "intent"-klassificering
                som ibland skilde sig från backend-verkligheten (t.ex. "Vilande
                user" → "Ingen ändring" när användaren redan finns). Klient-
                valideringen lever vidare i fel/varnings-boxarna ovan. */}

            {/* Action-knapparna flyttades till en konsoliderad rad längst ner i
                steget — under tabellen så de blir "next step" efter att användaren
                scrollat igenom raderna. Vänster = navigation, höger = primära åtgärder. */}

            {dryRunResult && (
              <div className="mt-4">
                {dryRunResult.blockingErrors?.length > 0 && (
                  <div className="bg-airpark-strawberry-50 border border-airpark-strawberry-500 rounded-lg p-4 mb-3">
                    <div className="font-medium text-airpark-strawberry-500 mb-2">
                      {t("assignment.blockingErrors")}
                    </div>
                    <ul className="text-sm text-airpark-strawberry-500 space-y-1">
                      {dryRunResult.blockingErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {(() => {
                  // Backend returnerar 0 för alla aggregat när det finns blocking errors
                  // (rollback-läge). Fallback: räkna från rader-arrayen för totalRows
                  // och visa "—" för actions som backend inte hann räkna.
                  const hasBlockers = (dryRunResult.blockingErrors?.length || 0) > 0;
                  const rowsLen = dryRunResult.rows?.length || 0;
                  const showOrDash = (v) => hasBlockers ? "—" : (v ?? 0);
                  const stats = [
                    { label: t("assignment.totalRows"), value: dryRunResult.totalRows || rowsLen },
                    { label: t("assignment.newPermits"), value: showOrDash(dryRunResult.newPermits) },
                    { label: t("assignment.updatedPermits"), value: showOrDash(dryRunResult.updatedPermits) },
                    { label: t("assignment.transitions"), value: showOrDash(dryRunResult.transitions) },
                    { label: t("assignment.dormantUsers"), value: showOrDash(dryRunResult.dormantUsersCreated) },
                    { label: t("assignment.noChange"), value: showOrDash(dryRunResult.noChangeRows) },
                    { label: t("assignment.ignored"), value: showOrDash(dryRunResult.ignoredRows) },
                  ];
                  return (
                    <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-4">
                      {stats.map((stat, i) => (
                        <div key={i} className="bg-airpark-gray-300 rounded-lg p-3 text-center">
                          <div className="text-xs text-airpark-gray-500">{stat.label}</div>
                          <div className="text-lg font-medium mt-1">{stat.value}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}

              </div>
            )}

            {/* Unified editable table — renderas alltid när rawRows finns.
                Editable cells för Telefon/Regnr/Från/Till. Action-kolumnen
                fylls från senaste dry-run; saknas dry-run visas klient-kind. */}
            {rawRows && rawRows.length > 0 && (() => {
              const dryRunByIdx = new Map();
              (dryRunResult?.rows || []).forEach((r) => dryRunByIdx.set(r.rowIndex, r));
              const totalPages = Math.ceil(rawRows.length / PAGE_SIZE);
              const pageRows = rawRows.slice(dryRunPage * PAGE_SIZE, (dryRunPage + 1) * PAGE_SIZE);
              const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";

              const classifyRow = (row) => {
                if (!hasValue(row["Telefonnummer"])) return "ignored";
                const hasFrom = hasValue(row["Från-datum"]);
                const hasTo = hasValue(row["Till-datum"]);
                if (!hasFrom && !hasTo) return "dormantOnly";
                return "activate";
              };

              const fmtCellDate = (v) => {
                if (!v) return null;
                const d = parseDate(v);
                return d ? d.toISOString().slice(0, 10) : String(v);
              };

              return (
                <div className="mt-2">
                  <div className="text-sm text-airpark-gray-500 mb-2 flex flex-wrap items-center gap-3">
                    <span>{t("assignment.tableHelp") || "Klicka på en cell för att redigera direkt."}</span>
                    {editedRowIndexes.size > 0 && (
                      <span className="text-airpark-yellow font-medium">
                        ✏️ {t("assignment.editedRowsLabel", { count: editedRowIndexes.size })}
                      </span>
                    )}
                  </div>
                  <div className="overflow-x-auto -mx-6">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-airpark-gray-300">
                          <th className="text-left py-2 px-3 w-12">#</th>
                          <th className="text-left py-2 px-3">{t("assignment.colSpot")}</th>
                          <th className="text-left py-2 px-3">{t("assignment.colObjectId")}</th>
                          <th className="text-left py-2 px-3">{t("assignment.colPhone")}</th>
                          <th className="text-left py-2 px-3">{t("assignment.colReg")}</th>
                          <th className="text-left py-2 px-3">{t("assignment.colFromDate")}</th>
                          <th className="text-left py-2 px-3">{t("assignment.colToDate")}</th>
                          <th className="text-left py-2 px-3">{t("assignment.colPrice") || "Pris"}</th>
                          <th className="text-left py-2 px-3">{t("assignment.colThirdParty") || "Annan part"}</th>
                          <th className="text-left py-2 px-3">{t("assignment.colAction") || "Status"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.map((row, pageIdx) => {
                          const idx = dryRunPage * PAGE_SIZE + pageIdx;
                          const dryRunRow = dryRunByIdx.get(idx);
                          const isEdited = editedRowIndexes.has(idx);
                          const kind = classifyRow(row);
                          // Per-cell-fel/varning från klient-validering — driver highlight på cell-nivå
                          const cellResult = clientValidation?.rowResults?.[idx] || { cellErrors: {}, cellWarnings: {} };
                          const cellErrors = cellResult.cellErrors;
                          const cellWarnings = cellResult.cellWarnings;
                          // Action: från senaste dry-run om tillgänglig; annars klient-kind
                          let actionLabel, actionColor, actionBg, explanation;
                          if (dryRunRow) {
                            const action = dryRunRow.overlapBlock ? "OverlapBlock" : dryRunRow.action;
                            const info = ACTION_LABEL[action] || ACTION_LABEL.Ignored;
                            actionLabel = info[lang];
                            actionColor = info.color;
                            actionBg = info.bg;
                            explanation = dryRunRow.message;
                          } else {
                            const kindMap = {
                              ignored: { label: t("assignment.kindIgnored") || "Hoppas över", color: "#6b7280", bg: "#f3f4f6" },
                              dormantOnly: { label: t("assignment.kindDormant") || "Vilande user", color: "#a16207", bg: "#fef9c3" },
                              activate: { label: t("assignment.kindActivate") || "Tillstånd", color: "#16a34a", bg: "#dcfce7" },
                            }[kind];
                            actionLabel = kindMap.label;
                            actionColor = kindMap.color;
                            actionBg = kindMap.bg;
                            explanation = null;
                          }

                          const rowBg = kind === "ignored" ? { opacity: 0.55 } : null;
                          const platsnummer = row["Platsnummer"] || row["🔒 Platsnummer"] || "—";
                          const objektId = row["Objekt-id"] || row["🔒 Objekt-id"] || "—";

                          return (
                            <tr key={idx} className="border-t border-airpark-gray-300" style={rowBg}>
                              <td className="py-2 px-3 text-airpark-gray-500">
                                {idx + 2}
                                {isEdited && <span title={t("assignment.editedTooltip") || "Ändrad i portalen"} className="ml-1">✏️</span>}
                              </td>
                              <td className="py-2 px-3">{platsnummer}</td>
                              <td className="py-2 px-3">{objektId}</td>
                              <td className="py-2 px-3">
                                <EditableCell
                                  value={row["Telefonnummer"]}
                                  type="text"
                                  placeholder="+46701234567"
                                  onSave={(v) => updateRow(idx, "Telefonnummer", v)}
                                  error={cellErrors.phone}
                                  warning={cellWarnings.phone}
                                />
                              </td>
                              <td className="py-2 px-3">
                                <EditableCell
                                  value={row["Regnummer"]}
                                  type="text"
                                  placeholder="ABC123"
                                  onSave={(v) => updateRow(idx, "Regnummer", v ? v.toUpperCase() : v)}
                                />
                              </td>
                              <td className="py-2 px-3">
                                <EditableCell
                                  value={row["Från-datum"]}
                                  displayValue={fmtCellDate(row["Från-datum"]) || "—"}
                                  type="date"
                                  onSave={(v) => updateRow(idx, "Från-datum", v)}
                                  error={cellErrors.startDate}
                                  warning={cellWarnings.startDate}
                                />
                              </td>
                              <td className="py-2 px-3">
                                <EditableCell
                                  value={row["Till-datum"]}
                                  displayValue={fmtCellDate(row["Till-datum"]) || "—"}
                                  type="date"
                                  onSave={(v) => updateRow(idx, "Till-datum", v)}
                                  error={cellErrors.endDate}
                                  warning={cellWarnings.endDate}
                                />
                              </td>
                              <td className="py-2 px-3">
                                {/* Annan part = Ja → priset nollas vid utfärdande (R3/grupp C),
                                    så visa "—" och gör cellen icke-redigerbar: det du ser = det som händer. */}
                                {(row["Annan part (Ja/Nej)"] || "").toString().trim().toLowerCase() === "ja" ? (
                                  <span className="text-airpark-gray-500" title="Pris hanteras av annan part — debiteras inte av AirPark">—</span>
                                ) : (
                                  <EditableCell
                                    value={row["Pris (kr/månad)"]}
                                    displayValue={row["Pris (kr/månad)"] ? `${row["Pris (kr/månad)"]} kr/mån` : "—"}
                                    type="text"
                                    placeholder="0"
                                    onSave={(v) => updateRow(idx, "Pris (kr/månad)", v)}
                                  />
                                )}
                              </td>
                              <td className="py-2 px-3">
                                <EditableCell
                                  value={row["Annan part (Ja/Nej)"]}
                                  displayValue={row["Annan part (Ja/Nej)"] || "Nej"}
                                  options={["Ja", "Nej"]}
                                  onSave={(v) => updateRow(idx, "Annan part (Ja/Nej)", v)}
                                />
                              </td>
                              <td className="py-2 px-3">
                                <span style={{
                                  backgroundColor: actionBg,
                                  color: actionColor,
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  whiteSpace: "nowrap",
                                }}>
                                  {actionLabel}
                                </span>
                                {explanation && (
                                  <div className="text-xs text-airpark-gray-500 mt-1">{explanation}</div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {totalPages > 1 && (
                    <div className="mt-4">
                      <Paging
                        totalNumberOfPages={totalPages}
                        currentPage={dryRunPage}
                        setCurrentPage={setDryRunPage}
                      />
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Konsoliderad action-rad längst ner — navigation till vänster,
                primära åtgärder till höger. Download visas bara om något editats;
                Utfärda visas bara efter lyckad "Kontrollera mot systemet". */}
            <div className="flex gap-3 mt-6 justify-between flex-wrap">
              <div className="flex gap-3 flex-wrap">
                <ButtonText isSecondary onClick={() => setActiveStep(1)}>
                  {t("assignment.back") || "Tillbaka"}
                </ButtonText>
                <ButtonText isSecondary onClick={handleReset}>
                  {t("assignment.startOver") || "Börja om"}
                </ButtonText>
              </div>
              <div className="flex gap-3 flex-wrap">
                {editedRowIndexes.size > 0 && (
                  <ButtonText isSecondary onClick={handleDownloadEdited}>
                    {t("assignment.downloadEdited") || "Ladda ner uppdaterad mall"}
                  </ButtonText>
                )}
                <ButtonText
                  onClick={handleDryRun}
                  disabled={isRunning || (clientValidation && clientValidation.hasErrors)}
                >
                  {dryRunResult ? (t("assignment.rerunDryRun") || "Kontrollera igen") : t("assignment.dryRun")}
                </ButtonText>
                {dryRunResult && dryRunResult.blockingErrors?.length === 0 && dryRunResult.wasDryRun && (
                  <ButtonText onClick={handleExecute} disabled={isRunning}>
                    {t("assignment.execute")}
                  </ButtonText>
                )}
              </div>
            </div>
          </div>
        )}

        {/* STEG 4: Klart */}
        {activeStep === 3 && dryRunResult && !dryRunResult.wasDryRun && (
          <div className="flex flex-col gap-4 items-center text-center py-6">
            <div className="w-12 h-12 rounded-full bg-airpark-green-background flex items-center justify-center">
              <svg className="w-6 h-6 text-airpark-green" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-xl font-medium">{t("assignment.executeResult")}</h3>
            <div className="text-sm text-airpark-gray-500 max-w-md">
              {dryRunResult.newPermits} {t("assignment.newPermits")}, {dryRunResult.updatedPermits} {t("assignment.updatedPermits")}, {dryRunResult.transitions || 0} {t("assignment.transitions")}, {dryRunResult.dormantUsersCreated} {t("assignment.dormantUsers")}.
            </div>
            <ButtonText onClick={handleReset}>
              {t("assignment.uploadAnother") || "Ladda upp en till"}
            </ButtonText>
          </div>
        )}
      </Widget>

      <ImportFromExternalSystem
        display={showImportModal}
        onClose={() => setShowImportModal(false)}
        parkingAreaExtId={parkingAreaExtId}
        spots={spots}
        onConverted={(rows) => {
          setRawRows(rows);
          setDryRunResult(null);
          setShowImportModal(false);
          showToast(t("assignment.importedFromExternal", { count: rows.length }), "", "success");
          setActiveStep(2);
        }}
      />
    </>
  );
};

export default AssignmentPage;
